import React, { useCallback, useEffect, useState } from "react"
import { usePaymentService } from "../../../hooks/use-payment-service"
import { useOrderStatusSSE } from "../../../services/use-order-status-sse"
import { useTranslation } from "react-i18next"
import { translationKeys } from "../../../util/translationKeys"
import SSESubscriberService from "../../../services/SSESubscriberService"
import { CheckoutOrder } from "../../../models/order"

export type RenderChildParameters = {
  paymentProcessing: boolean
  setPaymentProcessing: React.Dispatch<React.SetStateAction<boolean>>
  startTimeout: (
    providerName: string,
    preCancelAction: () => void | boolean
  ) => void
  sseSubscriber: React.MutableRefObject<SSESubscriberService | undefined>
  timeoutId: React.MutableRefObject<NodeJS.Timeout | undefined>
  setTimeoutId: (val: NodeJS.Timeout | undefined) => void
}

type Props = {
  renderChild: (args: RenderChildParameters) => JSX.Element
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void
  showPaymentSucceededScreen: () => void
  order: CheckoutOrder
}

export const SSEWrapper = ({
  renderChild,
  showPaymentFailedScreen,
  showPaymentSucceededScreen,
  order,
}: Props) => {
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const [_timeoutId, _setTimeoutId] = useState<NodeJS.Timeout | undefined>(
    undefined
  )
  const sseSubscriberRef = React.useRef<SSESubscriberService | undefined>()
  const timeoutId = React.useRef(_timeoutId)
  // Using states in event listeners will always read initial state, so we have to update a ref in order for the event listener to access the latest state
  const setTimeoutId = (val: NodeJS.Timeout | undefined) => {
    timeoutId.current = val
    _setTimeoutId(val)
  }

  const { t } = useTranslation()

  const { startCancelPaymentTimeout } = usePaymentService()
  const { subscribe, sseSubscriber } = useOrderStatusSSE(
    timeoutId,
    order,
    showPaymentFailedScreen,
    showPaymentSucceededScreen,
    setTimeoutId,
    setPaymentProcessing
  )
  subscribe()

  const startTimeout = useCallback(
    (providerName: string, preCancelAction: () => void | boolean) => {
      const onCancelAttempted = (hasCancelled: boolean) => {
        if (hasCancelled) {
          showPaymentFailedScreen(
            `${t(translationKeys.paymentTimedOut)}`,
            providerName
          )
        }
        setPaymentProcessing(false)
      }

      const onError = (errorMessage?: string) => {
        showPaymentFailedScreen(errorMessage, providerName)
      }

      const timeOutId = startCancelPaymentTimeout(
        order.order.id,
        showPaymentSucceededScreen,
        onError,
        preCancelAction,
        onCancelAttempted
      )
      setPaymentProcessing(true)
      setTimeoutId(timeOutId)
    },
    [sseSubscriber]
  )

  // We need to update the ref when sseSubscriber changes, as event listener cannot access the latest state due to the way closures work in JS / TS
  // Event listners can access latest reference of a ref, but not the latest state of a state
  useEffect(() => {
    if (!sseSubscriber) {
      return
    }
    sseSubscriberRef.current = sseSubscriber
  }, [sseSubscriber])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [])

  return renderChild({
    paymentProcessing,
    setPaymentProcessing,
    startTimeout,
    sseSubscriber: sseSubscriberRef,
    timeoutId: timeoutId,
    setTimeoutId,
  })
}
